import { bildschirmWechsel } from "./api/nutzung";
import { MotionConfig } from "motion/react";
import {
  createBrowserRouter,
  Navigate,
  Outlet,
  RouterProvider,
  ScrollRestoration,
  useLocation,
  type Location,
} from "react-router";
import { abmeldungBewusst, useIch } from "./api/abfragen";
import { fassungOffen } from "./api/fassung";
import { CircleAlert } from "lucide-react";
import { Knopf } from "./design/Knopf";
import { Laedt, Leer } from "./design/Leer";
import { Schale } from "./design/Schale";
import { ToastAnbieter } from "./design/Toast";
import { Anmelden } from "./seiten/Anmelden";
import { Posteingang } from "./seiten/Posteingang";

const OEFFENTLICH = {
  "passwort-vergessen": "PasswortVergessen",
  "passwort-neu": "PasswortNeu",
  einladung: "EinladungAnnehmen",
  bestaetigen: "Bestaetigen",
} as const;

/** Seiten ausser Anmeldung und Posteingang werden erst beim Aufruf geladen (pdf.js nur mit dem
 *  Dokument). */
function seite<M>(laden: () => Promise<M>, name: keyof M) {
  return async () => ({
    Component: (await laden())[name] as React.ComponentType,
  });
}

/** Statt der Standard-Fehlerseite des Routers (Verifier W4, F9): meist ist die App inzwischen neu
 *  ausgeliefert oder das Netz weg, dann hilft Neuladen. */
function Ladefehler() {
  // W82 Teil 4 (Vorlage R11 „zustaende“ Ladefehler): mittig im Bildschirm über der Tab-Leiste (welt/rest.css)
  return (
    <main className="ladefehler rand mx-auto max-w-(--w-readable) pt-16">
      <Leer icon={CircleAlert} titel="Diese Seite ließ sich nicht laden." aktion={<Knopf onClick={() => window.location.reload()}>Neu laden</Knopf>}>
        Vielleicht ist die Verbindung weg oder es gibt eine neue Version der App.
      </Leer>
    </main>
  );
}

/** Nur mit Sitzung; sonst zur Anmeldung (mit Rueckweg). Nach der Registrierung zuerst die
 *  Einfuehrung (W9), die ohne App-Huelle als eigene Seite laeuft. */
function Angemeldet() {
  const { data: ich, isPending } = useIch();
  const ort = useLocation();
  if (isPending) return <Laedt />;
  if (!ich)
    // R1b W82 Web-6b: nach bewusstem Abmelden ohne Rückweg (sonst führte die Anmeldung zurück in die Einstellungen)
    return <Navigate to="/anmelden" replace state={abmeldungBewusst() ? null : { von: ort.pathname }} />;
  const einfuehrung = ort.pathname === "/willkommen";
  if (ich.eingefuehrt === false && !einfuehrung) return <Navigate to="/willkommen" replace />;
  if (einfuehrung) return <Outlet />;
  // W17b: hat das Konto einer aelteren Fassung der Rechtstexte zugestimmt, fragt die App einmal je
  // Anmeldung; „Jetzt nicht“ fuehrt zurueck in die App
  const neueFassung = ort.pathname === "/neue-fassung";
  if (fassungOffen(ich) && !neueFassung) return <Navigate to="/neue-fassung" replace />;
  if (neueFassung) return fassungOffen(ich) ? <Outlet /> : <Navigate to="/" replace />;
  return (
    <Schale>
      <Outlet />
    </Schale>
  );
}

/** W82-2 N1 (S6): beim Wechsel des Pfads oben anfangen, Zurück stellt die gemerkte Rolllage wieder her (Router). Auf
 *  demselben Pfad (Suche, Filter des Archivs, Zustand eines Briefs oder Chats per Ersetzen) bleibt die Lage: der neue
 *  Eintrag liest die eben gemerkte Lage des alten. */
function rollSchluessel(ort: Location): string {
  const jetzt = router.state.location;
  return ort.pathname === jetzt.pathname ? jetzt.key : ort.key;
}

function Wurzel() {
  return (
    <>
      <ScrollRestoration getKey={rollSchluessel} />
      <Outlet />
    </>
  );
}

const router = createBrowserRouter([{ element: <Wurzel />, children: [
  { path: "/anmelden", element: <Anmelden />, errorElement: <Ladefehler /> },
  // W20: Link aus der Anmelde-Mail, eingeloest erst auf Knopfdruck. W74 (D-282): im Browser angefordert kommt er als
  // `/anmelden/link/web`; den Pfad beansprucht die iOS-App nicht, er oeffnet also im Browser; eingeloest wird erst auf
  // „Jetzt anmelden“ (D-368)
  ...(["/anmelden/link", "/anmelden/link/web"] as const).map((pfad) => ({
    path: pfad,
    lazy: seite(() => import("./seiten/Oeffentlich"), "AnmeldeLink"),
    hydrateFallbackElement: <Laedt />,
    errorElement: <Ladefehler />,
  })),
  // W68: Rueckweg von Apple und Google, ohne Anmeldung (verbinden und bestaetigen bringen ihre Sitzung mit)
  {
    path: "/anmelden/rueckweg",
    lazy: seite(() => import("./seiten/AnbieterRueckweg"), "AnbieterRueckweg"),
    hydrateFallbackElement: <Laedt />,
    errorElement: <Ladefehler />,
  },
  // W9: Links aus Mails, ohne Anmeldung
  ...(["passwort-vergessen", "passwort-neu", "einladung", "bestaetigen"] as const).map((pfad) => ({
    path: `/${pfad}`,
    lazy: seite(() => import("./seiten/Oeffentlich"), OEFFENTLICH[pfad]),
    hydrateFallbackElement: <Laedt />,
    errorElement: <Ladefehler />,
  })),
  {
    path: "/status",
    lazy: seite(() => import("./seiten/Systemstatus"), "Systemstatus"),
    hydrateFallbackElement: <Laedt />,
  },
  // W82 (Teil Web-1): Vorschau der Bausteine der W82-Welt, ohne Anmeldung, nicht verlinkt, nur Musterdaten. Nur im
  // Vorschau-Modus (Bau mit VITE_W82_VORSCHAU=1: Prüfbau in pruefen.sh, Vorschau der Handprobe); der Bau für den Betrieb
  // enthält weder Route noch Seite (R1 W82-Web-1, Festlegung im Start-Hinweis)
  ...(import.meta.env.VITE_W82_VORSCHAU === "1"
    ? [
        {
          path: "/vorschau/bausteine",
          lazy: seite(() => import("./seiten/VorschauBausteine"), "VorschauBausteine"),
          hydrateFallbackElement: <Laedt />,
          errorElement: <Ladefehler />,
        },
      ]
    : []),
  // W15: Rechtsseiten, ohne Anmeldung
  ...(
    [
      ["impressum", "Impressum"],
      ["datenschutz", "Datenschutz"],
      ["nutzungsbedingungen", "Nutzungsbedingungen"],
    ] as const
  ).map(([pfad, name]) => ({
    path: `/${pfad}`,
    lazy: seite(() => import("./seiten/rechtliches/Rechtliches"), name),
    hydrateFallbackElement: <Laedt />,
    errorElement: <Ladefehler />,
  })),
  // W17c: Widerrufsfunktion nach § 356a BGB, ohne Anmeldung erreichbar
  {
    path: "/vertrag-widerrufen",
    lazy: seite(() => import("./seiten/rechtliches/Widerruf"), "Widerruf"),
    hydrateFallbackElement: <Laedt />,
    errorElement: <Ladefehler />,
  },
  {
    element: <Angemeldet />,
    hydrateFallbackElement: <Laedt />,
    errorElement: <Ladefehler />,
    children: [
      {
        // Fehler einer Seite (etwa ein Nachlade-Fehler ohne Netz) innerhalb der App-Huelle zeigen
        errorElement: <Ladefehler />,
        children: [
      { path: "/", element: <Posteingang /> },
      {
        path: "/dokument/:id",
        lazy: seite(
          () => import("./seiten/DokumentAnsicht"),
          "DokumentAnsicht",
        ),
      },
      {
        path: "/blaettern",
        lazy: seite(() => import("./seiten/Blaettern"), "Blaettern"),
      },
      { path: "/akten", lazy: seite(() => import("./seiten/Akten"), "Akten") },
      {
        path: "/akten/:id",
        lazy: seite(() => import("./seiten/AkteAnsicht"), "AkteAnsicht"),
      },
      { path: "/briefe", lazy: seite(() => import("./seiten/Briefe"), "Briefe") },
      {
        path: "/briefe/:id",
        lazy: seite(() => import("./seiten/BriefBearbeiten"), "BriefBearbeiten"),
      },
      {
        path: "/finanzen",
        lazy: seite(() => import("./seiten/Finanzen"), "Finanzen"),
      },
      {
        path: "/fristen",
        lazy: seite(() => import("./seiten/Fristen"), "Fristen"),
      },
      {
        path: "/aufgaben",
        lazy: seite(() => import("./seiten/Aufgaben"), "Aufgaben"),
      },
      { path: "/suche", lazy: seite(() => import("./seiten/Suche"), "Suche") },
      { path: "/assistent", lazy: seite(() => import("./seiten/Assistent"), "Assistent") },
      {
        path: "/assistent/:id",
        lazy: seite(() => import("./seiten/AssistentChat"), "AssistentChat"),
      },
      {
        path: "/erfassen",
        lazy: seite(() => import("./seiten/Erfassen"), "Erfassen"),
      },
      {
        path: "/einstellungen",
        lazy: seite(() => import("./seiten/Einstellungen"), "Einstellungen"),
      },
      // FW7 (D-436): Darstellung als Unterseite der Einstellungen
      {
        path: "/einstellungen/darstellung",
        lazy: seite(() => import("./seiten/Darstellung"), "Darstellung"),
      },
      {
        path: "/post-per-mail",
        lazy: seite(() => import("./seiten/PostPerMail"), "PostPerMail"),
      },
      { path: "/plan", lazy: seite(() => import("./seiten/Plan"), "Plan") },
      { path: "/haushalt", lazy: seite(() => import("./seiten/Haushalt"), "Haushalt") },
      { path: "/konto", lazy: seite(() => import("./seiten/DatenKonto"), "DatenKonto") },
      { path: "/freigaben", lazy: seite(() => import("./seiten/Freigaben"), "Freigaben") },
      { path: "/protokoll", lazy: seite(() => import("./seiten/Protokoll"), "Protokoll") },
      { path: "/willkommen", lazy: seite(() => import("./seiten/Einfuehrung"), "Einfuehrung") },
      { path: "/neue-fassung", lazy: seite(() => import("./seiten/NeueFassung"), "NeueFassung") },
      { path: "/kennenlernen", lazy: seite(() => import("./seiten/Kennenlernen"), "Kennenlernen") },
      { path: "/sortieren", lazy: seite(() => import("./seiten/Sortieren"), "Sortieren") },
      { path: "/betrieb", lazy: seite(() => import("./seiten/Betrieb"), "Betrieb") },
      // W82 Beta-W: Rückmeldungen für das Betreiberkonto (für alle anderen 404 wie eine unbekannte Seite)
      { path: "/betrieb/rueckmeldungen", lazy: seite(() => import("./seiten/BetriebRueckmeldungen"), "BetriebRueckmeldungen") },
      { path: "/betrieb/rueckmeldungen/:id", lazy: seite(() => import("./seiten/BetriebRueckmeldungen"), "BetriebRueckmeldungen") },
      { path: "*", element: <Navigate to="/" replace /> },
        ],
      },
    ],
  },
] }]);

// W15 Nutzungsstatistik: jeder Wechsel des Bildschirms mit dem Muster der Route, nie mit IDs
function routenMuster(): string | null {
  const letzte = router.state.matches.at(-1);
  const pfad = letzte?.route.path;
  return pfad && pfad !== "*" ? pfad.replace(/:\w+/g, "{id}") : pfad === undefined && letzte ? "/" : null;
}
let zuletztGemeldet: string | null = null;
function bildschirmMelden() {
  if (router.state.navigation.state !== "idle") return;
  const name = routenMuster();
  if (name === zuletztGemeldet) return;
  zuletztGemeldet = name;
  bildschirmWechsel(name);
}
router.subscribe(bildschirmMelden);
bildschirmMelden();

export function App() {
  return (
    <MotionConfig reducedMotion="user">
      <ToastAnbieter navigieren={(zu) => void router.navigate(zu)}>
        <RouterProvider router={router} />
      </ToastAnbieter>
    </MotionConfig>
  );
}
